import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Game } from '../domain/game';
import {
  CollectionOption,
  CollectionOptionId
} from './game-collection-option.model';

const collectionGroups: Record<string, CollectionOptionId[]> = {
  'data options': [
    'is_assists_collection',
    'goal_clips',
    'is_highlight_collection',
    'isExportVideoReviewClips'
  ],
  'collection options': [
    'is_live_collection',
    'lagClock',
    'auto_puck_possession_events',
    'gameTimeImport',
    'is_realtime_collection',
    'is_cv_live_foint_collection'
  ],
  'Marker and other options': [
    'videoReview',
    'auto_camera_sync',
    'is_training_game',
    'is_sihf_live',
    'disable_updates',
    'enable_extra_lines'
  ]
};

// Must match the default of the server's auto camera sync (camera-auto-sync.service.ts)
const AUTO_CAMERA_SYNC_BEFORE_MINUTES = 60;

/**
 * When the auto camera sync runs for this game: from its start in venue time
 * once the game has a start and a venue time zone, otherwise relative to the start.
 */
export function autoCameraSyncTooltip(game: Game): string {
  const requirement = 'Requires a live \'main\' camera';
  const start = game.date ? new Date(game.date) : undefined;
  if (!start || isNaN(start.getTime()) || !game.venueUTCOffset) {
    return `Syncs the camera offsets every 5 minutes from 1 h before the game start until game end or 4h duration. ${requirement}`;
  }
  const from = new Date(
    start.getTime() - AUTO_CAMERA_SYNC_BEFORE_MINUTES * 60_000
  );
  const fromTime = formatDate(from, 'HH:mm', 'en-US', game.venueUTCOffset);
  return `Syncs the camera offsets every 5 minutes from ${fromTime} until game end (1 h before until game end or 4h duration). ${requirement}`;
}

const collectionOptions: CollectionOption[] = [
  {
    id: 'is_live_collection',
    fieldName: 'isLiveCollection',
    label: 'Live Collection',
    tooltip:
      'Optimizes user interface for live collection, adjusts required validations'
  },
  {
    id: 'is_highlight_collection',
    fieldName: 'isHighlightCollection',
    label: 'Highlight Collection',
    tooltip: 'Enables highlight events and rating column'
  },
  {
    id: 'is_realtime_collection',
    fieldName: 'isRealtimeCollection',
    label: 'Realtime',
    tooltip:
      'Optimizes FOINT/Shot event types for fast, uninterrupted creation of draft events, requires 2nd-pass'
  },
  {
    id: 'is_cv_live_foint_collection',
    fieldName: 'isCVLiveFOINTCollection',
    label: 'Live CV FO/INT',
    tooltip:
      'Optimizes Face-Off & Interruption input for Computer Vision assisted collection'
  },
  {
    id: 'auto_puck_possession_events',
    fieldName: 'autoPuckPossessionEvents',
    label: 'Automated Neutral Puck Possession',
    tooltip:
      'Automatically adds neutral puck possession events directly after face-offs, shots and passes (chip, rim, incomplete)',
    condition: (game: Game) =>
      !game.dataSet || (game.dataSet !== 'LITE' && game.dataSet !== 'LITE_PLUS')
  },
  {
    id: 'goal_clips',
    fieldName: 'goalClips',
    label: 'Goal Clips',
    tooltip: 'Requires enriching goals with slow motion sequences'
  },
  {
    id: 'is_assists_collection',
    fieldName: 'isAssistsCollection',
    label: 'Assists',
    tooltip: 'Requires enriching goals with assists'
  },
  {
    id: 'is_sihf_live',
    fieldName: 'isSihfLive',
    label: 'SIHF Live Export',
    tooltip: 'Exports data to SIHF Game Center in realtime'
  },
  {
    id: 'disable_updates',
    fieldName: 'disableUpdates',
    label: 'Disable Automated SIHF Updates',
    tooltip: 'Prevents SIHF game lineup from being overridden',
    condition: (game: Game) => game.idType === 'SIHF' && !!game.gameId
  },
  {
    id: 'is_training_game',
    fieldName: 'isTrainingGame',
    label: 'Training Game',
    tooltip: 'Data is not exported (Data Cockpit, SIHF, others)'
  },
  {
    id: 'lagClock',
    fieldName: 'lagClock',
    label: 'Show Lag Clock',
    tooltip: 'Shows a clock with difference to live in the video view'
  },
  {
    id: 'videoReview',
    fieldName: 'isVideoReview',
    label: 'Video Review',
    tooltip: 'Enables game in VJ360'
  },
  {
    id: 'auto_camera_sync',
    fieldName: 'isAutoCameraSync',
    label: 'Auto Camera Sync',
    tooltip: autoCameraSyncTooltip
  },
  {
    id: 'enable_extra_lines',
    fieldName: 'enableExtraLines',
    label: 'Enable Extra Lines (6–8)',
    tooltip: 'Activates additional lines and related tools'
  },
  {
    id: 'gameTimeImport',
    fieldName: 'isGameTimeImport',
    label: 'Import SIHF GameTime',
    tooltip: 'Enables import of SIHF game time as period start/end events',
    condition: (game: Game) => game.idType === 'SIHF' && !!game.gameId
  },
  {
    id: 'isExportVideoReviewClips',
    fieldName: 'isExportVideoReviewClips',
    label: 'Video Review Clips',
    tooltip: 'Requires enriching highlight events of type "Video Review"',
    condition: (game: Game) => game.isHighlightCollection && game.isVideoReview
  }
];

@Injectable({
  providedIn: 'root'
})
export class GameCollectionOptionService {
  constructor() {}

  getAllCollectionOptions() {
    return [...collectionOptions];
  }

  getAllCollectionGroups() {
    return { ...collectionGroups };
  }

  getCollectionGroups(game: Game) {
    const groups = [];
    Object.keys(collectionGroups).forEach((key) => {
      groups[key] = collectionGroups[key].filter((optionId) => {
        const option: CollectionOption = this.findOption(optionId);
        return !option?.condition || option?.condition(game);
      });
    });
    return groups;
  }

  tooltip(option: CollectionOption, game: Game): string {
    return typeof option.tooltip === 'function'
      ? option.tooltip(game)
      : option.tooltip;
  }

  collectionOptions(optionIds: CollectionOptionId[]) {
    return optionIds.map((optionId) => this.findOption(optionId));
  }

  private findOption(optionId: CollectionOptionId) {
    return collectionOptions.find((option) => option.id === optionId);
  }
}
